/**
 * Companion types for TransferBorrowerService.
 *
 * Mirrors the backend eligibility vocabulary defined in the
 * CheckTransferEligibility operation. Field names in TransferValidation
 * follow the codebase's camelCase response convention.
 */

export enum TransferStage {
  SEARCHING,
  VALIDATING,
  INELIGIBLE,
  VALIDATED,
  EXECUTING,
  COMPLETE,
  EXECUTION_FAILED,
}

export enum EligibilityErrorCode {
  SOURCE_NOT_TEST          = 'SOURCE_NOT_TEST',
  TARGET_NOT_ONBOARDED     = 'TARGET_NOT_ONBOARDED',
  SAME_BORROWER            = 'SAME_BORROWER',
  TARGET_HAS_DOCUMENTS     = 'TARGET_HAS_DOCUMENTS',
  NO_TARGET_ACCESS         = 'NO_TARGET_ACCESS',
  NO_SOURCE_ACCESS         = 'NO_SOURCE_ACCESS',
  SOURCE_HAS_NO_DOCUMENTS  = 'SOURCE_HAS_NO_DOCUMENTS',
  CROSS_TENANT_TRANSFER    = 'CROSS_TENANT_TRANSFER',
}

/**
 * The view-model the search component and the modal render bands from.
 * `code` is the union of BE eligibility codes plus two client-only codes
 * (`NO_SELECTION` is owned by the modal, `NETWORK_ERROR` by the service's
 * HTTP-error branch).
 */
export interface TransferErrorView {
  code: EligibilityErrorCode | 'NO_SELECTION' | 'NETWORK_ERROR' | 'SOURCE_NOT_FOUND';
  copy: string;
  viewBorrowerUuid?: string;
}

export interface TransferValidation {
  source: { uuid: string; name: string };
  target: {
    uuid: string;
    name: string;
    externalId: string;
    portfolio: string;
    sicCode: string;
    region: string;
    createdDate: string;
  };
  eligibility: {
    ok: boolean;
    errors: { code: EligibilityErrorCode; message: string }[];
  };
  periods: Array<{
    uuid: string;
    statementDate: string;
    scenario: string;
    scenarioName: string;
    monthsCovered: number;
    preparationType: string;
    reportingInterval: string;
    hidden: boolean;
  }>;
  documents: Array<{
    uuid: string;
    name: string;
    status: string;
    uploadDate: string;
    completionDate: string | null;
    archived: boolean;
  }>;
}

/** Declared for forward-compat with the future execute endpoint. */
export interface TransferResult {
  targetUuid: string;
  periodsTransferred: number;
  documentsTransferred: number;
}

/**
 * Code → copy + link-flag map. Only the 8 server codes; NO_SELECTION
 * and NETWORK_ERROR are inlined at their call sites (modal builds
 * the NO_SELECTION view; service builds the NETWORK_ERROR view).
 */
export const EligibilityErrorMessages:
  Record<EligibilityErrorCode, { copy: string; hasViewLink: boolean }> = {
  [EligibilityErrorCode.TARGET_HAS_DOCUMENTS]: {
    copy: 'Data cannot be transferred to this borrower as it contains existing documents. All documents must be deleted from the destination borrower before proceeding.',
    hasViewLink: true,
  },
  [EligibilityErrorCode.NO_TARGET_ACCESS]: {
    copy: 'Data cannot be transferred to this borrower as you do not have access to it. Please reach out to prod support for more details.',
    hasViewLink: false,
  },
  [EligibilityErrorCode.TARGET_NOT_ONBOARDED]: {
    copy: 'Selected borrower is not onboarded. Choose an onboarded borrower (with GFCID).',
    hasViewLink: false,
  },
  [EligibilityErrorCode.SOURCE_NOT_TEST]: {
    copy: 'This borrower is already onboarded and cannot be the source of a transfer.',
    hasViewLink: false,
  },
  [EligibilityErrorCode.SAME_BORROWER]: {
    copy: 'Source and target must be different borrowers.',
    hasViewLink: false,
  },
  [EligibilityErrorCode.NO_SOURCE_ACCESS]: {
    copy: 'You no longer have access to the source borrower. Refresh and try again.',
    hasViewLink: false,
  },
  [EligibilityErrorCode.SOURCE_HAS_NO_DOCUMENTS]: {
    copy: 'Source borrower has no documents to transfer.',
    hasViewLink: false,
  },
  [EligibilityErrorCode.CROSS_TENANT_TRANSFER]: {
    copy: 'Selected borrower belongs to a different tenant. Please contact support.',
    hasViewLink: false,
  },
};

/** Convenience: client-only network error copy, used by the service. */
export const NETWORK_ERROR_COPY = 'Validation failed. Please try again.';

/** Convenience: client-only no-selection copy, used by the modal. */
export const NO_SELECTION_COPY = 'Please select a borrower to continue.';

/** Used when execute returns a non-eligibility error OR a malformed 200 envelope. */
export const EXECUTION_FAILED_COPY =
  'Transfer failed. No data has been moved. Your source borrower is unchanged. '
  + 'Please try again or contact support if the issue persists.';

/** Used when execute returns 404 SOURCE_NOT_FOUND — the transfer may have already
 * succeeded in a prior call but the response was lost. The modal shows an info toast
 * and routes to the target borrower's financials view.
 */
export const SOURCE_NOT_FOUND_COPY = 'Transfer may have already completed. Loading borrower...';

/**
 * BE wraps the validation payload in the standard api_response envelope.
 */
export interface ValidateEnvelope {
  errors: Record<string, unknown>;
  meta: unknown;
  response: { objects: TransferValidation[] };
}

/** BE wraps the execute response (success or error) in the standard api_response envelope.
 * Note: every response — including 400-eligibility and 404-SOURCE_NOT_FOUND — uses this
 * shape (the api_response helper applies it regardless of HTTP status). For error bodies
 * the `objects[0]` payload differs: success has TransferResult, 400 has {eligibility},
 * 404 has {errorCode: 'SOURCE_NOT_FOUND'}.
 */
export interface ExecuteEnvelope {
  errors: Record<string, unknown>;
  meta: unknown;
  response: { objects: TransferResult[] };
}
